import type { ReactNode } from 'react';
import { useState, useEffect } from 'react';

import type { Provider } from '@atlaskit/collab-provider/provider';
import type { CollabEditProvider } from '@atlaskit/editor-common/collab';

import { fetchContentAppearanceProperty, updateAppearanceForCollab } from './contentAppearance.ts';
import type {
	ContentAppearance,
	UpdateContentAppearancePropertyBody,
} from './contentAppearanceTypes.ts';

type ContentAppearanceButtonProps = {
	children: React.ReactNode;
	userOffline?: boolean;
	params: UpdateContentAppearancePropertyBody;
	collabEditProvider?: CollabEditProvider | (CollabEditProvider & Provider) | null;
	currentContentAppearance?: ContentAppearance;
	setContentAppearance: (appearance: ContentAppearance) => void;
};

export const ContentAppearanceButton = ({
	children,
	userOffline,
	params,
	collabEditProvider,
	currentContentAppearance,
	setContentAppearance,
}: ContentAppearanceButtonProps): ReactNode => {
	const [shouldFetchAppearance, setShouldFetchAppearance] = useState<boolean>(false);

	const fetchAndUpdateAfterOffline = async () => {
		const appearanceProperty = await fetchContentAppearanceProperty(
			params.contentId,
			params.propertyKey,
			'network-only',
		);

		const currentStateVersion = currentContentAppearance?.version || 0;

		if (appearanceProperty?.version > currentStateVersion) {
			const newParams = {
				...params,
				propertyValue: appearanceProperty.appearance,
				versionNumber: appearanceProperty.version,
			};

			// Just need to update the state if it's out of date, no need to perform a mutation to update the current view
			setContentAppearance(appearanceProperty);
			updateAppearanceForCollab(collabEditProvider, newParams);
		}
	};

	useEffect(() => {
		// should refetch contentAppearanceProperty after user reconnected
		setShouldFetchAppearance(!!userOffline);
	}, [userOffline]);

	if (!userOffline && shouldFetchAppearance) {
		void fetchAndUpdateAfterOffline();
	}

	return children;
};
